import { computed, Directive, inject, OnDestroy, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { SortState } from '@modules/components/table/table';
import { FilterService } from '@modules/filter/services/filter/filter.service';
import { showSpinner } from '@modules/spinner/spinner';
import { SEARCH_CONTROL } from '@tokens/search-contorl.token';
import { PostRequest } from '@type/request.type';
import { debounceTime, EMPTY, Observable, switchMap, tap } from 'rxjs';
import { CompanyStateService } from 'src/app/pages/companies/services/company-state.service';

const DEFAULT_PAGINATOR = {
  pageIndex: 0,
  pageSize: environment.perPage,
};

const DEFAULT_SORTING: SortState = {
  active: '',
  direction: '',
};

@Directive({
  selector: '[tablePage]',
})
export abstract class TablePageDirective<D> implements OnDestroy {
  abstract displayedColumns: Signal<Array<keyof D | 'actions' | 'select'>>;

  protected abstract makeRequest(request: PostRequest): Observable<D[]>;

  readonly pagination = signal(DEFAULT_PAGINATOR);
  readonly sort = signal(DEFAULT_SORTING);
  readonly update = signal(Date.now());
  readonly length = signal(0);

  readonly searchControl = inject(SEARCH_CONTROL);
  readonly company = inject(CompanyStateService).company;

  protected readonly filterService = inject(FilterService);
  protected readonly search = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  isLoaded = signal(false);

  private releaseInitialSpinner = showSpinner({ selectorById: 'spinner-container' });

  isFiltered = computed(
    () => Boolean(this.search()?.trim()) || Object.keys(this.filterService.filterForRequest()?.q ?? {}).length > 0,
  );

  readonly computedRequest = computed((): PostRequest => {
    const { pageIndex, pageSize } = this.pagination();
    const sort = this.sort();

    return {
      update: this.triggerUpdate(),
      per_page: pageSize,
      sorting: this.createSortingQuery(sort),
      page: pageIndex + 1,
      q: this.buildQuery(),
    };
  });

  readonly dataSource: Signal<D[]> = toSignal(
    toObservable(this.computedRequest).pipe(
      debounceTime(500),
      switchMap((request) => (this.shouldFetch() ? this.fetch(request) : EMPTY)),
    ),
    { initialValue: [] as D[] },
  );

  private fetch(request: PostRequest): Observable<D[]> {
    const markLoaded = () => {
      this.isLoaded.set(true);
      this.releaseInitialSpinner();
    };

    return this.makeRequest(request).pipe(tap({ next: markLoaded, error: markLoaded }));
  }

  /**
   * Override to plug in a custom update trigger signal (e.g. a service-level update counter).
   * The return value is tracked inside computedRequest, so any signal read here causes a recompute.
   */
  protected triggerUpdate(): number {
    return this.update();
  }

  /**
   * Override to build the `q` part of the request.
   * Any signals read inside this method are automatically tracked by computedRequest.
   */
  protected shouldFetch(): boolean {
    return true;
  }

  protected buildQuery(): Record<string, unknown> {
    return {};
  }

  createSortingQuery(sort: SortState): string {
    return sort.direction ? `${sort.active} ${sort.direction}` : '';
  }

  ngOnDestroy(): void {
    this.releaseInitialSpinner();
    this.searchControl.reset('', { emitEvent: false });
  }
}
